// useUserTier — read the current user's active tier via the
// public.user_active_tier() RPC.
//
// Anon callers: tierCode=null, isPro=false, loading=false.
//
// `hasTier` is the convenience helper for component-level gating
// that needs an exact-tier check. Use the pure `tierMeets(...)` from
// ./tier-gates.ts for hierarchy-aware gating (higher tier covers lower).
//
// Doctrine: never fabricate. If the RPC returns null or errors, the
// surface treats the user as free-tier (no Pro access).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuthSession } from "./useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";
import { tierMeets, type TierGate } from "./tier-gates.ts";

export interface UserTierState {
  tierCode: string | null;
  hasTier: (code: TierGate) => boolean;
  isPro: boolean;
  loading: boolean;
  refetch: () => Promise<void>;
}

export function useUserTier(): UserTierState {
  const { user, loading: authLoading } = useAuthSession();
  const supabase = useMemo(() => getBrowserClient(), []);
  const [tierCode, setTierCode] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  // True once we've resolved the tier at least once. Supabase fires
  // onAuthStateChange (TOKEN_REFRESHED) every time the window regains focus,
  // which changes the `user` object identity and re-runs fetchTier. If we
  // flipped `loading` true on those background refetches, any tier gate that
  // renders a full-screen <Loading/> (e.g. the Slip Extractor page) would
  // UNMOUNT its subtree and wipe all in-progress state. So only block the UI
  // on the very first resolve; refetches update tierCode silently.
  const settledOnce = useRef(false);

  const fetchTier = useCallback(async (): Promise<void> => {
    if (!user) {
      setTierCode(null);
      setLoading(false);
      settledOnce.current = true;
      return;
    }
    if (!settledOnce.current) setLoading(true);
    try {
      // Source-of-truth: paid subscriptions in `user_subscriptions` via the
      // `user_active_tier()` RPC (Phase H). If a paid tier is active, use it.
      // Cast: generated DB types do not yet include the paystack RPCs.
      const { data, error } = await (supabase.rpc as unknown as (
        fn: string,
      ) => Promise<{ data: string | null; error: unknown }>)("user_active_tier");
      const paidTier =
        !error && typeof data === "string" && data.length > 0 ? data : null;
      if (paidTier) {
        setTierCode(paidTier);
        return;
      }
      // Fallback: JB.1 trial / founder states live on `profiles.tier_state`
      // (no `user_subscriptions` row is written for trials). Treat
      // `trial`/`pro`/`founder` as conferring sika_pro access so trial
      // surfaces don't get blocked by the paywall.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const sb = supabase as any;
      const profileRes = await sb
        .from("profiles")
        .select("tier_state")
        .eq("id", user.id)
        .maybeSingle();
      const tierState =
        profileRes && typeof profileRes.data?.tier_state === "string"
          ? (profileRes.data.tier_state as string)
          : null;
      if (tierState === "trial" || tierState === "pro" || tierState === "founder") {
        setTierCode("sika_pro");
      } else {
        setTierCode(null);
      }
    } catch {
      setTierCode(null);
    } finally {
      setLoading(false);
      settledOnce.current = true;
    }
  }, [supabase, user]);

  useEffect(() => {
    if (authLoading) return;
    void fetchTier();
  }, [authLoading, fetchTier]);

  const hasTier = useCallback(
    (code: TierGate): boolean => tierMeets(tierCode, code),
    [tierCode],
  );

  const isPro = tierCode !== null && tierMeets(tierCode, "sika_pro");

  return {
    tierCode,
    hasTier,
    isPro,
    loading: authLoading || loading,
    refetch: fetchTier,
  };
}
